"use client";

import React from "react";
import { Loader, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
function EligibilityFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-2">
          <CircleCheck className="size-8 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="font-serif text-2xl font-bold text-foreground">
          Request Received
        </h3>
        <p className="text-muted-foreground max-w-md">
          {successMessage}
        </p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-semibold">Name</FormLabel>
              <FormControl>
                <Input placeholder="First & Last Name" className="bg-background/50 border-input focus-visible:ring-secondary focus-visible:border-secondary transition-all duration-300 h-12 text-base" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-semibold">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="you@yourdomain.com" className="bg-background/50 border-input focus-visible:ring-secondary focus-visible:border-secondary transition-all duration-300 h-12 text-base" {...field} />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground mt-1.5">
                Enter your email so Nexus Group can securely send your personalized timeshare-exit eligibility results.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        minLength: {
          value: 10,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-semibold">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" className="bg-background/50 border-input focus-visible:ring-secondary focus-visible:border-secondary transition-all duration-300 h-12 text-base" {...field} />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground mt-1.5">
                Provide a phone number where our trusted advisors can reach you to discuss your exit strategy.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="resort" rules={{
        required: "Resort selection is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-semibold">Select Your Resort</FormLabel>
              <FormControl>
                <select {...field} className="flex h-12 w-full items-center justify-between rounded-md border border-input bg-background/50 px-3 py-2 text-base shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-300 appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22currentColor%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:1.25rem] bg-[right_0.75rem_center] bg-no-repeat">
                  <option value="" disabled>Select a resort</option>
                  <option value="Wyndham">Wyndham</option>
                  <option value="Marriott">Marriott</option>
                  <option value="Hilton">Hilton</option>
                  <option value="Westgate">Westgate</option>
                  <option value="Bluegreen">Bluegreen</option>
                  <option value="Diamond Resorts">Diamond Resorts</option>
                  <option value="Disney Vacation Club">Disney Vacation Club</option>
                  <option value="Holiday Inn Club Vacations">Holiday Inn Club Vacations</option>
                  <option value="Vistana (Sheraton/Westin)">Vistana (Sheraton/Westin)</option>
                  <option value="Other">*Not Sure / Other*</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="wereYouMisled" rules={{
        required: "This field is required"
      }} render={({
        field
      }) => <FormItem className="space-y-3">
              <FormLabel className="text-foreground/80 font-semibold">
                Were You Misled During The Sales Process? * (yes or no)
              </FormLabel>
              <FormControl>
                <div className="flex gap-6 mt-2">
                  {["Yes", "No"].map((option, i) => <label key={option} data-index={i} className="flex items-center gap-2 cursor-pointer group">
                      <input type="radio" className="size-4 accent-secondary border-input cursor-pointer" checked={field.value === option} onChange={() => field.onChange(option)} name="wereYouMisled" />
                      <span className="text-foreground/80 group-hover:text-foreground transition-colors">
                        {option}
                      </span>
                    </label>)}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="hasMortgage" rules={{
        required: "This field is required"
      }} render={({
        field
      }) => <FormItem className="space-y-3">
              <FormLabel className="text-foreground/80 font-semibold">
                Do You Have A Mortgage On Your Timeshare? (yes or no)
              </FormLabel>
              <FormControl>
                <div className="flex gap-6 mt-2">
                  {["Yes", "No"].map((option, i) => <label key={option} data-index={i} className="flex items-center gap-2 cursor-pointer group">
                      <input type="radio" className="size-4 accent-secondary border-input cursor-pointer" checked={field.value === option} onChange={() => field.onChange(option)} name="hasMortgage" />
                      <span className="text-foreground/80 group-hover:text-foreground transition-colors">
                        {option}
                      </span>
                    </label>)}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-semibold rounded-lg bg-secondary text-secondary-foreground shadow-lg shadow-secondary/20 hover:bg-secondary/90 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Processing Request...
            </> : "Check Eligibility Now"}
        </Button>
        <p className="text-center text-xs text-muted-foreground mt-4 flex items-center justify-center gap-1.5">
          <CircleCheck className="size-3.5" />
          Your information is secure and confidential.
        </p>
      </div>
    </Form>;
}
export default function FormSection() {
  return <section id="form" className="bg-background relative py-24 md:py-32 overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-5xl h-full pointer-events-none">
        <div className="absolute top-1/4 -left-24 w-96 h-96 bg-secondary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-24 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 max-w-4xl relative z-10">
        <div className="text-center mb-12 md:mb-16 space-y-4">
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold text-foreground tracking-tight">
            Check Eligibility
          </h2>
          <p className="text-muted-foreground tracking-widest uppercase text-sm md:text-base font-semibold">
            Secure Your Exit Strategy Today
          </p>
        </div>

        <div className="relative">
          <div className="bg-card/80 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-2xl shadow-shadow-color/10 rounded-2xl p-8 md:p-12 relative z-10">
            <FormProvider formId="fast_eligibility_form_checkeligibility" sectionName="Eligibility Check Form" defaultValues={{
            name: "",
            email: "",
            phone: "",
            resort: "",
            wereYouMisled: "",
            hasMortgage: ""
          }} submitText="Check Eligibility Now" successMessage="Thank you. Our trusted advisors are reviewing your details and will contact you shortly to discuss your exit strategy." errorMessage="We encountered an error submitting your request. Please try again or contact us directly.">
              <EligibilityFormInner />
            </FormProvider>
          </div>
          
          <div className="absolute -inset-1 bg-gradient-to-b from-secondary/20 to-transparent rounded-[1.25rem] blur-sm -z-10 opacity-50" />
        </div>
      </div>
    </section>;
}